import { Divider, Grid, LinearProgress, makeStyles, Toolbar, Typography } from "@material-ui/core";
import { useQuery } from "@redux-requests/react";
import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { BaseLayout, Header, Loader, NoData, NoSessionData } from "../components";
import CardItem from "../components/CardItem";
import AutocompleteField from "../components/formik/AutocompleteField";
import MultipleAutocompleteField from "../components/formik/MultipleAutocompleteField";
import { types } from "../ducks";
import { getLiveSessions, getPastSessions, getProfiles, getTags } from "../ducks/actions";
import { dateTimeInterval } from "../ducks/utils";
import { useQueryParams } from "../hooks";
import useSyncInterval from "../hooks/useSyncInterval";
import { ReactComponent as SessionSvg } from "../assets/icons/session.svg";

const useStyles = makeStyles(theme => ({
  root: {
    "& > *": {
      marginBottom: theme.spacing(3)
    }
  },
  filter: {
    minWidth: 240,
    marginLeft: theme.spacing(1),
    flexGrow: 0.2
  },
  infiniteScroll: {
    overflow: "hidden !important"
  },
  toolbar: {
    overflow: "auto",
    "& > *": {
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: "auto"
    }
  }
}));

const SessionsPage = () => {
  const intl = useIntl();
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const [{ tags, profileId }, mergeParams] = useQueryParams();

  const [perPage] = useState(24);
  const [pastSessionPage, setPastSessionPage] = useState(1);

  useEffect(() => {
    dispatch(getLiveSessions());
  }, [dispatch]);

  const [isPolling, setIsPolling] = useState(false);
  useSyncInterval(() => {
    setIsPolling(true);
    dispatch(getLiveSessions());
    dispatch(getPastSessions({ tags, profileId, page: pastSessionPage, perPage }));
  });

  useEffect(() => {
    dispatch(getPastSessions({ tags, profileId, page: pastSessionPage, perPage }));
  }, [dispatch, profileId, pastSessionPage, tags, perPage]);

  const {
    data: {
      data: pastSessions,
      meta: { total: totalPastSessions }
    },
    loading: pastSessionsLoading
  } = useQuery({ type: types.GET_PAST_SESSIONS, defaultData: { data: [], meta: { total: 0 } } });

  const {
    data: { data: liveSessions },
    loading: liveSessionsLoading
  } = useQuery({ type: types.GET_LIVE_SESSIONS, defaultData: { data: [] } });

  const onSubmit = ({ tags, profileId }) => {
    setPastSessionPage(1);
    mergeParams({ tags, profileId });
  };

  useEffect(() => {
    dispatch(getTags({ resource: "sessions" }));
    dispatch(getProfiles({ role: "speaker" }));
  }, [dispatch]);

  const {
    data: { data: profiles },
    loading: profilesLoading
  } = useQuery({ type: types.GET_PROFILES, defaultData: { data: [] } });

  const {
    data: { data: availableTags },
    loading: tagsLoading
  } = useQuery({ type: types.GET_TAGS, defaultData: { data: [] } });

  const profileOptions = profiles.map(({ id, firstName, lastName }) => ({
    label: `${firstName} ${lastName}`,
    value: id
  }));

  const tagOptions = availableTags.map(({ name }) => ({
    label: name,
    value: name
  }));

  const nextSessionPage = () => {
    setPastSessionPage(parseInt(pastSessionPage, 10) + 1);
  };

  return (
    <BaseLayout>
      <InfiniteScroll
        className={classes.infiniteScroll}
        scrollableTarget="scrollerContent"
        dataLength={pastSessions.length}
        next={nextSessionPage}
        hasMore={pastSessions.length < totalPastSessions}
        loader={
          <>
            <LinearProgress />
            <br />
          </>
        }
        endMessage={
          <p style={{ textAlign: "center" }}>
            <b>
              {intl.formatMessage({
                id: "Pages.SessionsPage.InfiniteScrollEnd",
                defaultMessage: "All sessions are shown!"
              })}
            </b>
          </p>
        }
      >
        <div className={classes.root}>
          <Header
            icon={SessionSvg}
            title={intl.formatMessage({
              id: "Pages.SessionsPage.Sessions",
              defaultMessage: "Sessions"
            })}
          />
          <Typography variant="h5">
            {intl.formatMessage({
              id: "Pages.SessionsPage.LiveNow",
              defaultMessage: "Live now"
            })}
          </Typography>
          <Loader loading={liveSessionsLoading && !isPolling}>
            <NoData
              message={intl.formatMessage({
                id: "Pages.SessionsPage.NoLiveSessionData",
                defaultMessage:
                  "There are no sessions currently live. Please check on the program to see the upcoming sessions..."
              })}
              data={liveSessions}
            >
              <Grid container spacing={3}>
                {liveSessions.map(({ id, title, thumbUrl, programEvent, exhibitors }) => (
                  <Grid key={id} item xs={12} sm={6} md={4} lg={3}>
                    <CardItem
                      title={title}
                      thumbUrl={thumbUrl}
                      description={intl.formatMessage(
                        {
                          id: "Pages.SessionsPage.Room",
                          defaultMessage: "Room {room} - {dateInterval}"
                        },
                        {
                          room: programEvent?.roomList?.join(", "),
                          dateInterval: dateTimeInterval(programEvent?.startAt, programEvent?.endAt)
                        }
                      )}
                      isLive
                      onClick={() => {
                        history.push(`/sessions/${id}`);
                      }}
                      sponsored={exhibitors && exhibitors?.length > 0}
                    />
                  </Grid>
                ))}
              </Grid>
            </NoData>
          </Loader>
          <Divider className={classes.divider} />
          <Typography variant="h5">
            {intl.formatMessage({
              id: "Pages.SessionsPage.OnDemand",
              defaultMessage: "On Demand"
            })}
          </Typography>
          <Formik
            initialValues={{
              tags: tags || [],
              profileId: profileId || ""
            }}
            onSubmit={onSubmit}
          >
            <Toolbar className={classes.toolbar}>
              <Loader loading={profilesLoading || tagsLoading}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={2} lg={1}>
                    <Typography variant="subtitle1">
                      <strong>
                        {intl.formatMessage({
                          id: "Pages.SessionsPage.FilterBy",
                          defaultMessage: "Filter by"
                        })}
                      </strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={5} lg={3} xl={2}>
                    <MultipleAutocompleteField
                      name="tags"
                      variant="outlined"
                      label={intl.formatMessage({
                        id: "Pages.SessionsPage.Tag",
                        defaultMessage: "Tag"
                      })}
                      className={classes.filter}
                      options={tagOptions}
                      submitOnChange
                    />
                  </Grid>
                  <Grid item xs={12} sm={5} lg={3} xl={2}>
                    <AutocompleteField
                      name="profileId"
                      variant="outlined"
                      label={intl.formatMessage({
                        id: "Pages.SessionsPage.Speaker",
                        defaultMessage: "Speaker"
                      })}
                      className={classes.filter}
                      options={profileOptions}
                      submitOnChange
                    />
                  </Grid>
                </Grid>
              </Loader>
            </Toolbar>
          </Formik>
          <Loader loading={pastSessionsLoading && !isPolling}>
            <NoSessionData data={pastSessions}>
              <Grid container spacing={3}>
                {pastSessions.map(
                  ({ id, title, description, thumbUrl, recordings, exhibitors }) => {
                    return (
                      <Grid key={id} item xs={12} sm={6} md={4} lg={3}>
                        <CardItem
                          title={title}
                          description={description}
                          thumbUrl={thumbUrl}
                          videoUrl={
                            Array.isArray(recordings) && recordings.length > 0
                              ? recordings[0].videoUrl
                              : null
                          }
                          onClick={() => {
                            history.push(`/sessions/${id}`);
                          }}
                          sponsored={exhibitors && exhibitors?.length > 0}
                        />
                      </Grid>
                    );
                  }
                )}
              </Grid>
            </NoSessionData>
          </Loader>
        </div>
      </InfiniteScroll>
    </BaseLayout>
  );
};

export default SessionsPage;
